import { useState, useMemo, useEffect } from "react";
import { Layers, Users, ClipboardList, CheckCircle2, Building2 } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { 
  useFetchDataCountQuery, 
  useFetchGenderGraphQuery, 
  useFetchRecentPatientsQuery, 
  useFetchRecentVisitsQuery, 
  useFetchUnitChartGraphQuery, 
  useFetchVisitChartQuery 
} from "../../stores/api/dashboard.api";
import { Counter } from "../../Components/Utils";
import { Link } from "react-router-dom";
import { ROUTE_PATHS } from "../../constants/routeConstants";
import { PatientVisitsCard } from "./Components/PatientVisitCard";
import { GenderPatientsChart } from "./Components/GenderPatientsChart";
import { RecentPatientTable } from "./Components/RecentPatientTable";
import { VisitChart } from "./Components/VisitChart";
import UnitDistributionChart from "./Components/UnitDistributionChart";
import { useUser } from "../../contexts/UserContext";

export default function Dashboard() {
  const [genderFilter, setGenderFilter] = useState("Current Week");
  const [visitFilter, setVisitFilter] = useState("Current Month"); // Default Month wise requested
  const { user, setUser } = useUser();
  const { data: dataCount, isFetching } = useFetchDataCountQuery();
  const { data: recentPatients, isFetching: isLoadingPatients } = useFetchRecentPatientsQuery();
  const { data: recentVisits, isFetching: isVisitsLoading } = useFetchRecentVisitsQuery();
  const { data: genderWiseGraph, isFetching: isGenderGraphLoading } = useFetchGenderGraphQuery({ filter: genderFilter });
  const { data: visitChartData, isFetching: isVisitChartLoading } = useFetchVisitChartQuery({ filter: visitFilter });
  const { data: unitChartData , isFetching: isUnitChartLoading } = useFetchUnitChartGraphQuery();
  console.log(unitChartData);
  const handleGenderFilterChange = (selectedOption) => {
    setGenderFilter(selectedOption);
  };

  const handleVisitFilterChange = (selectedOption) => {
    setVisitFilter(selectedOption);
  };

  const statCards = [
    { title: "Patients", value: 0, icon: Layers, bg: "bg-pink-50", iconBg: "bg-pink-100", iconColor: "text-pink-500", link: ROUTE_PATHS.PATIENT_LIST },
    { title: "Receptionists", value: 0, icon: Users, bg: "bg-blue-50", iconBg: "bg-blue-100", iconColor: "text-blue-500", link: ROUTE_PATHS.USERS },
    { title: "Lab Users", value: 0, icon: ClipboardList, bg: "bg-green-50", iconBg: "bg-green-100", iconColor: "text-green-500", link: ROUTE_PATHS.USERS },
    { title: "Physicians", value: 0, icon: CheckCircle2, bg: "bg-yellow-50", iconBg: "bg-yellow-100", iconColor: "text-yellow-500", link: ROUTE_PATHS.USERS },
    { title: "Units", value: 0, icon: Building2, bg: "bg-purple-50", iconBg: "bg-purple-100", iconColor: "text-purple-500", link: ROUTE_PATHS.UNIT_LIST },
  ];

  const [cardCount, setCardCount] = useState(statCards);
  
  useEffect(() => {
    if (!isFetching && dataCount) {
      setCardCount((prev) =>
        prev.map((card) => {
          switch (card.title) {
            case "Receptionists": return { ...card, value: dataCount.total_receptionists };
            case "Lab Users": return { ...card, value: dataCount.total_lab_users };
            case "Physicians": return { ...card, value: dataCount.total_physicians };
            case "Units": return { ...card, value: dataCount.total_units };
            case "Patients": return { ...card, value: dataCount.total_patients };
            default: return card;
          }
        })
      );
    }
  }, [dataCount, isFetching]);

  return (
    <div className="m-3 mt-4 space-y-6">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white md:text-3xl">
            Welcome back, <span className="bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-transparent dark:from-green-500 dark:to-green-200">{user?.name}</span> 👋
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Here's what's happening across your medical units today.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start rounded-2xl border border-slate-200/60 bg-white/50 p-2 px-3 shadow-sm backdrop-blur-sm dark:border-slate-800 dark:bg-slate-900/50 sm:self-auto">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
            System Live
          </span>
        </div>
      </div>

      {/* ───────── TOP 5 STATS ───────── */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:grid-cols-5">
        {cardCount.map((card, i) => {
          const Icon = card.icon;
          const getBorderColor = () => {
            if (card.iconColor?.includes('green')) return '#10b981';
            if (card.iconColor?.includes('blue')) return '#3b82f6';
            if (card.iconColor?.includes('pink') || card.iconColor?.includes('rose')) return '#f43f5e';
            if (card.iconColor?.includes('amber') || card.iconColor?.includes('yellow')) return '#eab308';
            if (card.iconColor?.includes('purple') || card.iconColor?.includes('violet')) return '#a855f7';
            return 'currentColor';
          };
          return (
            <Link
              to={card.link}
              key={i}
              className="group relative mt-2 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 dark:border-slate-800 dark:bg-slate-900 hover:border-[var(--hover-color)] dark:hover:border-[var(--hover-color)] hover:shadow-[0_12px_24px_-4px_rgba(var(--hover-rgb),0.2),0_4px_12px_-4px_rgba(var(--hover-rgb),0.15)]"
              style={{
                '--hover-color': getBorderColor(),
                '--hover-rgb': card.iconColor?.includes('emerald') ? '16,185,129' :
                  card.iconColor?.includes('blue') ? '59,130,246' :
                    card.iconColor?.includes('pink') || card.iconColor?.includes('rose') ? '244,63,94' :
                      card.iconColor?.includes('amber') || card.iconColor?.includes('yellow') ? '234,179,8' :
                        card.iconColor?.includes('purple') || card.iconColor?.includes('violet') ? '168,85,247' : '59,130,246'
              }}
            >
              <div className={`absolute -top-5 left-5 flex h-11 w-11 items-center justify-center rounded-xl shadow transition-all duration-300 group-hover:scale-110 ${card.iconBg} ${card.iconColor}`}>
                <div className="relative flex items-center justify-center">
                  <div className="absolute -inset-1.5 -z-10 h-8 w-8 rounded-full bg-current opacity-20 blur-sm animate-pulse group-hover:hidden" />
                  <Icon className="h-5 w-5 transition-transform duration-300 group-hover:[animation:blink_0.8s_infinite]" />
                  <div className="absolute inset-0 -z-10 h-5 w-5 scale-0 rounded-full bg-current opacity-0 transition-all duration-300 group-hover:scale-150 group-hover:opacity-20 group-hover:animate-ping" />
                </div>
              </div>

              <div className="pt-4">
                <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white">
                  {isFetching ? (
                    <span className="text-sm text-slate-400">Loading...</span>
                  ) : (
                    <Counter end={card.value} />
                  )}
                </h2>
                <p className="mt-2 text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  Total {card.title}
                </p>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="grid gap-6">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="flex flex-col lg:col-span-2 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
            <RecentPatientTable recentPatients={recentPatients} isLoadingPatients={isLoadingPatients} />
          </div>
          
          <GenderPatientsChart 
            chartResponse={genderWiseGraph} 
            isLoading={isGenderGraphLoading} 
            onFilterChange={handleGenderFilterChange} 
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="xl:col-span-2">
            <PatientVisitsCard patientVisits={recentVisits} isVisitsLoading={isVisitsLoading} />
          </div>
          
          <VisitChart 
            chartResponse={visitChartData} 
            isLoading={isVisitChartLoading} 
            onFilterChange={handleVisitFilterChange}
          />

           <UnitDistributionChart data={unitChartData?.unit_distribution} isLoading={isUnitChartLoading} />
        </div>
      </div>
    </div>
  );
}